<template>
  <button
    :type="isSubmit ? 'submit' : 'button'"
    class="base-button" :class="[
      `base-button--${type}`,
      `base-button--rounded-${rounded}`,
      { shadow: withShadow },
      { 'base-button__disabled': isDisabled },
      { 'base-button__loading': isLoading },
      { 'base-button__bold': isBold },
    ]"
    :style="{ width }"
    @click="clickButton"
  >
    <div
      v-if="isLoading"
      class="base-button__loading-spinner"
    />
    <div v-else class="base-button__content">
      <nuxt-icon v-if="arrow === 'back'" name="icon_arrow" filled />
      <slot />
      <span v-if="arrow === 'next'" style="min-height: 22px; min-width: 22px; display: flex;">
        <nuxt-icon name="icon_arrow" style="transform: rotate(180deg); color: #fff" />
      </span>
    </div>
  </button>
</template>

<script setup lang="ts">
const props = defineProps({
  type: {
    type: String,
    default: 'grey',
    validator(type: string): boolean {
      return ['grey', 'pink', 'dark', 'black', 'red', 'blue', 'whatsapp', 'text', 'green', 'white'].includes(type);
    },
  },
  rounded: {
    type: String,
    default: 'half',
    validator(type: string): boolean {
      return ['full', 'half'].includes(type);
    },
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: '100%',
  },
  isBold: {
    type: Boolean,
    default: true,
  },
  withShadow: {
    type: Boolean,
    default: false,
  },
  arrow: {
    type: String,
    default: '', // back, next
  },
  isSubmit: {
    type: Boolean,
    default: false,
  },
});
const emits = defineEmits(['click', 'disabled-click']);

function clickButton(): void {
  if (props.isDisabled || props.isLoading) {
    emits('disabled-click');
    return;
  }
  emits('click');
}
</script>

<style lang="scss" scoped>
.base-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border: none;
  text-align: center;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  position: relative;
  cursor: pointer;
  outline: 0;
  outline: none;
  -webkit-tap-highlight-color:transparent;
  -moz-tap-highlight-color:transparent;
  -o-tap-highlight-color:transparent;
  tap-highlight-color:transparent;

  &.fr {
    font-size: 16px;
  }

  &__bold {
    font-weight: 700;
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 8px;

    .nuxt-icon {
      line-height: 1;
    }
  }

  &__loading {
    min-height: 53px;
    pointer-events: none;
  }

  &__loading-spinner {
    border: 2px solid $white;
    border-radius: 50%;
    border-top: 4px solid transparent;
    height: 30px;
    width: 30px;
    animation: spin 2s linear infinite;
    position: absolute;
  }
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
}

.base-button--grey {
  background: $grey-button;
  color: $gray-text;
}

.base-button--pink {
  background: $pink;
  color: $white;
}

.base-button--black {
  background: $black-button;
  color: $white;
}

.base-button--dark {
  background: $dark-button;
  color: $white;
}

.base-button--red {
  background: $pink-button;
  color: $white;
}

.base-button--green {
  background: $green-button;
  color: $white;
}

.base-button--rounded-full {
  border-radius: 30px;
}

.base-button--rounded-half {
  border-radius: 9px;
}

.base-button--red.shadow {
  box-shadow: 0 15px 37px 0 rgba(228, 66, 64, 0.64);
}

.base-button--green.shadow {
  box-shadow: 0 15px 37px 0 rgba(57, 129, 139, 0.64);
}

.base-button--pink.shadow {
  box-shadow: 0px 15px 37px 0px rgba(228, 66, 64, 0.64);
}

.base-button--text {
  background-color: transparent;
}

.base-button--blue {
  background: $blue-button;
  color: $white;
}

.base-button--blue.shadow {
  box-shadow: 0 15px 37px 0 #5773D6;
}

.base-button--white {
  background: $white;
  color: $gray-text;
}

.base-button--text {
  background: transparent;
  padding: 0;
}

.base-button--whatsapp {
  background: $whatsapp-button;
  color: $white;
}

.base-button--whatsapp.shadow {
  box-shadow: 0 15px 37px 0 rgba(83, 204, 88, 0.64);
}

.base-button__disabled {
  background: #CACACA;
  box-shadow: none !important;
}
</style>
